import { Button, Card, Combobox, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { memo } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useStorefrontOptionsControls } from '../context/storefront-options-context';
import type { LocaleEntry, LocaleGroup } from '../types/spark-options';
import {
  entryValue,
  localeOptions,
  namedEntry,
  nextId,
  setEntryValue,
  updateEntry,
} from '../utils/locale-rows';
import { duplicateEntryIds, duplicateLocaleIds } from '../utils/locale-validation';
import { entryKeyError, localeError } from './locale-field-errors';
import { withTooltip } from './with-tooltip';

const EDITOR =
  'features:storefront_options.components.per_locale_fields_editor' satisfies I18nPrefix;
const LOCALE = 'features:storefront_options.components.locale_editor' satisfies I18nPrefix;

export type PerLocaleNamedField = {
  key: string;
  label: string;
  placeholder?: string;
  required?: boolean;
};

export const PerLocaleFieldsEditor = memo(function PerLocaleFieldsEditor({
  label,
  description,
  value,
  onChange,
  namedFields,
  allowFreeform = false,
  requireLocale = false,
}: {
  label?: string;
  description?: string;
  value: LocaleGroup[];
  onChange: (value: LocaleGroup[]) => void;
  namedFields: PerLocaleNamedField[];
  allowFreeform?: boolean;
  requireLocale?: boolean;
}) {
  const { showValidationErrors: showErrors } = useStorefrontOptionsControls();

  // Shown but not committed, so opening the page never marks it dirty.
  const groups = requireLocale && value.length === 0 ? [{ id: 0, locale: '', entries: [] }] : value;
  const duplicates = duplicateLocaleIds(groups);
  const canAddLocale = !groups.some((group) => group.locale.trim() === '');

  function updateGroup(id: number, next: LocaleGroup) {
    onChange(groups.map((group) => (group.id === id ? next : group)));
  }

  function updateEntries(group: LocaleGroup, entries: LocaleEntry[]) {
    updateGroup(group.id, { ...group, entries });
  }

  return (
    <div className="flex flex-col gap-3">
      {(label || description) && (
        <div className="flex flex-col">
          {label && <Text size="sm">{label}</Text>}
          {description && (
            <Text size="sm" variant="secondary">
              {description}
            </Text>
          )}
        </div>
      )}

      {groups.length === 0 && (
        <Text size="sm" variant="secondary">
          {t(`${EDITOR}.empty`)}
        </Text>
      )}

      {groups.map((group, index) => {
        // Only the rows the named inputs are bound to, so a repeat of a named key stays visible.
        const namedIds = new Set(
          namedFields.map((field) => namedEntry(group.entries, field.key)?.id),
        );
        const freeform = group.entries.filter((row) => !namedIds.has(row.id));
        const duplicateKeys = duplicateEntryIds(group.entries);
        const canAddFreeform = !freeform.some((row) => row.key.trim() === '');
        const canRemove = !requireLocale || index > 0;

        return (
          <Card size="sm" key={group.id}>
            <Card.Header
              headingLevel="h3"
              primaryAction={
                canRemove
                  ? {
                      variant: 'dangerBare',
                      icon: 'delete',
                      ariaLabel: t(`${LOCALE}.remove_language`),
                      onClick: () => onChange(groups.filter((current) => current.id !== group.id)),
                    }
                  : undefined
              }
            >
              {group.locale.trim() || t(`${LOCALE}.new_language`)}
            </Card.Header>

            <Card.Body>
              <div className="w-56 max-w-full">
                <Combobox
                  label={t(`${LOCALE}.locale`)}
                  options={localeOptions(groups, group.id)}
                  placeholder={t(`${LOCALE}.locale_placeholder`)}
                  value={group.locale || null}
                  allowCustomValue
                  error={localeError(group.locale, duplicates.has(group.id), showErrors)}
                  onChange={(next) => updateGroup(group.id, { ...group, locale: next ?? '' })}
                />
              </div>

              {namedFields.map((field) => {
                const text = entryValue(group.entries, field.key);
                const missing = showErrors && field.required && text.trim() === '';

                return (
                  <Input
                    key={field.key}
                    label={field.label}
                    placeholder={field.placeholder}
                    value={text}
                    error={
                      missing ? t(`${EDITOR}.field_required`, { field: field.label }) : undefined
                    }
                    onChange={(next) =>
                      updateEntries(group, setEntryValue(group.entries, field.key, next))
                    }
                  />
                );
              })}

              {allowFreeform && (
                <Card size="sm" hideBackground initialCollapsed={freeform.length === 0}>
                  <Card.Header headingLevel="h4" collapsible>
                    {t(`${EDITOR}.messages`)}
                  </Card.Header>

                  <Card.Body>
                    <Text size="sm" variant="secondary">
                      {t(`${EDITOR}.messages_hint`)}
                    </Text>

                    {freeform.length > 0 && (
                      <div className="grid grid-cols-[1fr_1fr_1.5rem] items-center gap-2">
                        <Text size="sm" variant="secondary">
                          {t(`${EDITOR}.message_key`)}
                        </Text>
                        <Text size="sm" variant="secondary">
                          {t(`${EDITOR}.message_value`)}
                        </Text>
                        <span aria-hidden />
                      </div>
                    )}

                    {freeform.map((row) => (
                      <div
                        className="grid grid-cols-[1fr_1fr_1.5rem] items-center gap-2"
                        key={row.id}
                      >
                        <Input
                          placeholder={t(`${EDITOR}.message_key_placeholder`)}
                          value={row.key}
                          error={entryKeyError(row.key, duplicateKeys.has(row.id), showErrors)}
                          onChange={(next) =>
                            updateEntries(group, updateEntry(group.entries, row.id, { key: next }))
                          }
                        />
                        <Input
                          placeholder={t(`${EDITOR}.message_value_placeholder`)}
                          value={row.value}
                          onChange={(next) =>
                            updateEntries(
                              group,
                              updateEntry(group.entries, row.id, { value: next }),
                            )
                          }
                        />
                        <Button
                          variant="dangerBare"
                          icon="delete"
                          ariaLabel={t(`${EDITOR}.remove_message`)}
                          onClick={() =>
                            updateEntries(
                              group,
                              group.entries.filter((current) => current.id !== row.id),
                            )
                          }
                        />
                      </div>
                    ))}

                    <div>
                      <Button
                        variant="secondary"
                        size="xs"
                        icon="plusCircle"
                        disabled={!canAddFreeform}
                        onClick={() =>
                          updateEntries(group, [
                            ...group.entries,
                            { id: nextId(group.entries), key: '', value: '' },
                          ])
                        }
                      >
                        {t(`${EDITOR}.add_message`)}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        );
      })}

      <div>
        {withTooltip(
          !canAddLocale && t(`${LOCALE}.add_language_disabled_hint`),
          <Button
            variant="secondary"
            size="xs"
            icon="plusCircle"
            disabled={!canAddLocale}
            onClick={() => onChange([...groups, { id: nextId(groups), locale: '', entries: [] }])}
          >
            {t(`${LOCALE}.add_language`)}
          </Button>,
        )}
      </div>
    </div>
  );
});
